/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Box from '@mui/material/Box';
import React, { useEffect, useState } from 'react';
import { FreedomWhiteIcon } from '../icons';
import { FormControlBase, SelectBase, StackBase, TypographyBase } from '@fmc-digital/ui';
import { MainMenuOptions } from '@types';
import { useDispatch, useSelector } from 'react-redux';
import {
    createAccountOptions,
    selectFilteredMenuOptions,
    selectGetSelectedLoanNumber,
    selectLoan,
    selectUserInfo
} from '@store/selectors/widgets.selectors';
import { MenuButton } from '../menu-button/MenuButton';
import { camelCase } from '@utils/addressFormat';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import { MobileHeaderLayout } from './mobileHeader';
import { setSelectedLoanNumber } from '@store/actions/widgets.actions';
import { get, isEmpty } from 'lodash';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { CodingConstants } from '@constants';
import { ExitIntent } from '../exit-intent/ExitIntent';
import { useVWOContext } from '../../../../context/VWOContext';

type BreadCrumbs = Array<{ id: string; name: string }>;
export type HeaderLayoutProps = {
    showDropDownOptions?: boolean;
    showMenuOptions?: boolean;
    title?: string;
    BreadCrumbs?: BreadCrumbs;
    showFooterAds?: boolean;
    optionalMenu?: boolean;
    useExitIntent?: boolean;
    onHeaderClickHandler?: (name: any) => void;
};

export const Breadcrumbs = ({
    crumbs,
    logoClickHandler,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    useExitIntent = false
}: {
    crumbs: BreadCrumbs;
    logoClickHandler: () => void;
    useExitIntent?: boolean;
}) => {
    const theme = useTheme();
    const [{ id: key, name: label }, ...rest] = crumbs;

    return (
        <StackBase direction="row" spacing={theme.spacing(0.5)} alignItems="center">
            <TypographyBase
                key={key}
                variant="caption-bold"
                onClick={logoClickHandler}
                sx={{ cursor: 'pointer', textDecorationLine: 'underline', color: theme.palette.primary.main }}
            >
                {label}
            </TypographyBase>
            {rest.map(({ id, name }) => {
                return (
                    <StackBase direction="row" spacing={theme.spacing(0.5)} alignItems="center" key={`${id}-stack-base`}>
                        <ChevronRightIcon sx={{ fontSize: '1.21875rem' }} />
                        <TypographyBase variant="caption" key={`${id}-typography-base`} fontFamily={'Poppins'}>
                            {name}
                        </TypographyBase>
                    </StackBase>
                );
            })}
        </StackBase>
    );
};
export const HeaderLayout = ({
    showDropDownOptions = true,
    showMenuOptions = true,
    BreadCrumbs = undefined,
    title = 'Welcome to your Freedom Account',
    optionalMenu = false,
    useExitIntent = false,
    onHeaderClickHandler
}: HeaderLayoutProps) => {
    const { state } = useLocation();
    const mainMenuOptions: MainMenuOptions[] = useSelector(state => selectFilteredMenuOptions(state, optionalMenu ? 'something' : null));
    const accountOptions = useSelector(createAccountOptions);
    const { RecordVWOEvent } = useVWOContext();
    const { firstName, lastName } = useSelector(selectUserInfo);
    const selected = useSelector(selectGetSelectedLoanNumber);
    const allLoans = useSelector(selectLoan);
    const dispatch = useDispatch();
    // const cards = useSelector(selectWidgetsData);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const onSelected = (event: any) => {
        const selectedLoan = allLoans.find(loan => loan.identifier === event.target.value);
        if (selectedLoan.type === 'OriginationLoanNumber') {
            navigate(`/new-loan/${selectedLoan.identifier}`);
        } else navigate('/');
        dispatch(setSelectedLoanNumber(selectedLoan));
    };
    const disabledAccountSelection = accountOptions.length < 2;
    const logoClickHandler = () => {
        if (!isEmpty(state) && state?.exitIntent && title === 'Error') {
            setOpen(true);
        } else if (useExitIntent && onHeaderClickHandler) {
            onHeaderClickHandler('');
        } else {
            navigate('/');
        }
    };
    // if (isEmpty(cards)) return null;
    useEffect(() => {
        RecordVWOEvent('HeaderComponentRendered', { product: 'ACP' });
    }, []);
    return (
        <>
            <Box
                sx={_theme => ({
                    background: _theme.palette.surfaceDark.main
                })}
            >
                {isMobile ? (
                    <MobileHeaderLayout
                        {...{
                            firstName,
                            lastName,
                            mainMenuOptions,
                            isServicing: get(selected, ['type'], 'unknown') === CodingConstants.servicingLoan,
                            logoClickHandler,
                            selectedLoanNumber: selected?.identifier,
                            onHeaderClickHandler
                        }}
                    />
                ) : (
                    <Box
                        sx={_theme => ({
                            display: 'flex',
                            padding: { xs: `${_theme.spacing(1)} ${_theme.spacing(2)}`, sm: `${_theme.spacing(2)} ${_theme.spacing(4)}` },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            alignSelf: 'stretch',
                            background: _theme.palette.surfaceDark.main,
                            borderBottom: `8px solid ${_theme.palette.teal.main}`
                        })}
                    >
                        <IconButton onClick={logoClickHandler} disableRipple>
                            <FreedomWhiteIcon sx={{ width: 157, height: 48 }} />
                        </IconButton>
                        {mainMenuOptions && showMenuOptions ? (
                            <StackBase direction="row" gap={4}>
                                {mainMenuOptions.map(menu => {
                                    return (
                                        <MenuButton
                                            {...menu}
                                            key={menu.id}
                                            isServicing={
                                                optionalMenu ? false : get(selected, ['type'], 'unknown') === CodingConstants.servicingLoan
                                            }
                                            selectedLoanNumber={selected?.identifier}
                                            onHeaderClickHandler={onHeaderClickHandler}
                                        />
                                    );
                                })}
                            </StackBase>
                        ) : null}
                    </Box>
                )}

                <Box
                    sx={theme => ({
                        display: 'flex',
                        padding: { xs: '0.5rem 1rem', sm: '0.5rem 2rem' },
                        justifyContent: { xs: 'flex-start', sm: 'center' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        // gap: '39.75rem',
                        background: theme.palette.neutrals[200]
                        // padding: `${theme.spacing(1)} ${theme.spacing(2)}`
                    })}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: isMobile ? 'flex-start' : 'center',
                            alignItems: 'flex-start',
                            gap: '0.25rem',
                            flex: '1 0 0'
                        }}
                    >
                        {isEmpty(BreadCrumbs) ? (
                            <TypographyBase variant="caption">{`Hi ${camelCase(`${firstName} ${lastName}` || '')}!`}</TypographyBase>
                        ) : (
                            <Breadcrumbs crumbs={BreadCrumbs} logoClickHandler={logoClickHandler} useExitIntent={useExitIntent} />
                        )}
                        <TypographyBase variant="subtitle1">{title}</TypographyBase>
                    </Box>
                    {showDropDownOptions && !isEmpty(accountOptions) ? (
                        <Box
                            sx={{
                                display: 'flex',
                                marginTop: isMobile && '1rem',
                                flexDirection: isMobile ? 'column' : 'row',
                                justifyContent: isMobile ? 'start' : 'center',
                                alignItems: isMobile ? 'start' : 'center',
                                gap: '1rem'
                            }}
                        >
                            <TypographyBase variant="caption-bold">Your Account(s):</TypographyBase>
                            <FormControlBase disabled={disabledAccountSelection}>
                                <SelectBase
                                    options={accountOptions}
                                    defaultValue={accountOptions[0].value}
                                    onChange={onSelected}
                                    value={isEmpty(selected) ? '' : selected.identifier}
                                    inputProps={{ 'aria-disabled': true }}
                                    disabled={disabledAccountSelection}
                                    sx={{
                                        '&:hover, &:focus': {
                                            borderColor: disabledAccountSelection ? theme.palette.surface.light : theme.palette.primary.main
                                        }
                                    }}
                                />
                            </FormControlBase>
                        </Box>
                    ) : null}
                </Box>
            </Box>
            <ExitIntent
                open={open}
                onContinue={() => {
                    setOpen(false);
                }}
            />
        </>
    );
};
